import { z } from 'zod';
import { msg, translate } from './messages';

/**
 * Schema primitives shared across forms. Only the rules that earned a shared home
 * live here — code-point length, required-and-capped text, and the DRBFM score
 * range; hand-rolling those per schema is where drift starts.
 *
 * Every cap here is a MIRROR of a backend or DB constraint and cites it.
 * Product-chosen limits belong in the feature schema, labelled as such — never
 * in this file, or a reader cannot tell an invented rule from an enforced one.
 */

/**
 * Length in CODE POINTS, which is what every cap in this file mirrors.
 *
 * JS `.length` — and therefore Zod's own `.max()`, which is `input.length`
 * (zod/v4/core/checks.cjs `$ZodCheckMaxLength`) — counts UTF-16 code units, so
 * one astral character costs two. Both sides of the boundary count code points:
 *   - Postgres `VARCHAR(n)`. Verified on the project DB (PG 16.14, UTF8):
 *     `repeat('😀',100)` — 200 UTF-16 units, 400 bytes — INSERTs into
 *     VARCHAR(100); 101 raises "value too long". So it is neither bytes nor
 *     UTF-16 units.
 *   - Python `len()` and `s[:n]`, which is how api/routes/documents.py:1241
 *     (`t.strip()[:100]`) truncates tags. Neither route encodes first.
 * Measuring UTF-16 units here rejects input the column accepts — a stricter
 * rule than the one being mirrored, and an invented one.
 *
 * NOT graphemes. `👨‍👩‍👧‍👦` is one user-perceived character but seven code
 * points, and VARCHAR(100) rejects twenty of them (140 code points). Counting
 * graphemes would accept values the column refuses — the same defect inverted.
 */
export const codePointLength = (value: string) => Array.from(value).length;

/**
 * A code-point-aware `.max()`. Drop it into any string chain as
 * `.check(maxCodePoints(n, key))` wherever `.max(n, msg(key, { max: n }))` used
 * to sit — the issue `code` becomes `custom` instead of `too_big`, everything
 * else (message key, `{{max}}` interpolation, position in the chain) is the same.
 *
 * It is a standalone check rather than a `.refine()` call because that is what
 * makes the rule ONE value: the predicate, the message and the cap travel
 * together to every call site. (Zod's `.refine()` METHOD would preserve the
 * `ZodString` type just as well — verified — but it takes two arguments, so
 * sharing it would mean a wrapper that swallows the schema and breaks the
 * chain's readability.)
 *
 * Both `.check()` and `.max()` return `this`, so `.optional()` / `.transform()`
 * still chain after it. Order still matters: checks run in sequence, so a
 * `.trim()` before this one is what gets measured, and a failed `.min(1)` does
 * not suppress it — an empty box reports `required` only because '' is also
 * under every cap, not because of any short-circuit.
 */
export const maxCodePoints = (max: number, tooLongKey: string) =>
  z.refine<string>((value) => codePointLength(value) <= max, msg(tooLongKey, { max }));

/** Required, trimmed, and capped. `max` must cite the column it mirrors. */
export const boundedText = (max: number, requiredKey: string, tooLongKey: string) =>
  z.string().trim().min(1, msg(requiredKey)).check(maxCodePoints(max, tooLongKey));

/** DRBFM review scores held as NUMBERS by the workbench cards. Pydantic ge=1 le=10 + DB CHECK. */
export const scoreRange1to10 = z
  .number()
  .int(msg('validation.integer'))
  .min(1, msg('validation.range', { min: 1, max: 10 }))
  .max(10, msg('validation.range', { min: 1, max: 10 }));

/**
 * scoreRange1to10 for a box the current ACTION requires even though the column is
 * nullable (confirming S/O/D needs O and D). The card normalises an empty box to
 * undefined before parsing, and undefined must read as "required" — never as zod's
 * "expected number, received undefined".
 */
export const requiredScoreRange1to10 = z
  .number({ error: (raised) => (raised.input === undefined ? translate('validation.required') : undefined) })
  .int(msg('validation.integer'))
  .min(1, msg('validation.range', { min: 1, max: 10 }))
  .max(10, msg('validation.range', { min: 1, max: 10 }));

